import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { FileText, Mail, Users, Download, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  
  const [submissions, setSubmissions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      setToken(response.data.token);
      setAuthenticated(true);
      toast({
        title: 'Success',
        description: 'Logged in successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Invalid credentials',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [submissionsRes, contactsRes, newsletterRes] = await Promise.all([
        axios.get(`${API}/submissions`),
        axios.get(`${API}/contact`),
        axios.get(`${API}/newsletter`)
      ]);
      
      setSubmissions(submissionsRes.data);
      setContacts(contactsRes.data);
      setNewsletter(newsletterRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    if (authenticated) {
      fetchData();
    }
  }, [authenticated]);

  const downloadSubmission = async (submissionId) => {
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `submission_${submissionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to download file',
        variant: 'destructive'
      });
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-page py-32 flex items-center justify-center">
        <Card className="w-full max-w-md bg-card-dark border-gold/20">
          <CardHeader>
            <CardTitle className="text-2xl text-gold text-center">Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-white">Username</Label>
                <Input
                  id="username"
                  value={loginData.username}
                  onChange={(e) => setLoginData(prev => ({ ...prev, username: e.target.value }))}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-white">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/20 text-white"
                />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page py-32">
      <div className="container mx-auto px-8">
        <div className="mb-8">
          <h1 className="heading-1 text-gold mb-4">Admin Dashboard</h1>
          <Button onClick={() => setAuthenticated(false)} variant="outline" className="text-gold border-gold">
            Logout
          </Button>
        </div>

        <Tabs defaultValue="submissions" className="w-full">
          <TabsList className="bg-card-dark border-gold/20">
            <TabsTrigger value="submissions" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <FileText className="mr-2 h-4 w-4" />
              Submissions ({submissions.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <Mail className="mr-2 h-4 w-4" />
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="data-[state=active]:bg-gold data-[state=active]:text-black">
              <Users className="mr-2 h-4 w-4" />
              Newsletter ({newsletter.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-6">
            <div className="grid gap-4">
              {submissions.map((submission) => (
                <Card key={submission.id} className="bg-card-dark border-gold/20">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="heading-5 text-gold mb-2">{submission.title}</h3>
                        <p className="body-small text-gray-300 mb-2">
                          <strong>Name:</strong> {submission.name} | <strong>Email:</strong> {submission.email}
                        </p>
                        <p className="body-small text-gray-300 mb-2">
                          <strong>Category:</strong> {submission.category}
                        </p>
                        <p className="body-small text-gray-400 mb-2">
                          {submission.synopsis.substring(0, 200)}...
                        </p>
                        <p className="caption text-gray-500">
                          Submitted: {new Date(submission.submission_date).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        onClick={() => downloadSubmission(submission.id)}
                        className="btn-secondary ml-4"
                        size="sm"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <div className="grid gap-4">
              {contacts.map((contact) => (
                <Card key={contact.id} className="bg-card-dark border-gold/20">
                  <CardContent className="p-6">
                    <h3 className="heading-6 text-gold mb-2">{contact.subject}</h3>
                    <p className="body-small text-gray-300 mb-2">
                      <strong>From:</strong> {contact.name} ({contact.email})
                    </p>
                    <p className="body-small text-gray-400 mb-2">{contact.message}</p>
                    <p className="caption text-gray-500">
                      Received: {new Date(contact.timestamp).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="newsletter" className="mt-6">
            <div className="grid gap-2">
              {newsletter.map((subscriber) => (
                <Card key={subscriber.id} className="bg-card-dark border-gold/20">
                  <CardContent className="p-4">
                    <p className="body-medium text-white">{subscriber.email}</p>
                    <p className="caption text-gray-500">
                      Subscribed: {new Date(subscriber.subscribed_date).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
